import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import CreatorStudioLayout from "./CreatorStudioLayout";
import { buildTransactionHistory, fetchCreatorDrops, fetchEarningsDoc } from "../../services/creatorStudioService";
import { calculateCreatorReceive, isFoundingCreator } from "../../services/dropPublishService";
import {
  computeMonthlyEarnings,
  computeTotalEarnings,
  formatMoney,
  formatMoneyWhole,
  nextPayoutDate,
  timeAgo,
} from "./creatorHelpers";

export default function Earnings() {
  const { currentUser, userProfile } = useAuth();
  const [drops, setDrops] = useState([]);
  const [earningsDoc, setEarningsDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    Promise.all([
      fetchCreatorDrops(currentUser.uid),
      fetchEarningsDoc(currentUser.uid),
    ])
      .then(([d, e]) => {
        setDrops(d);
        setEarningsDoc(e);
      })
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const founding = isFoundingCreator(userProfile);
  const monthStats = useMemo(() => computeMonthlyEarnings(drops, userProfile), [drops, userProfile]);
  const totalStats = useMemo(() => computeTotalEarnings(drops, userProfile), [drops, userProfile]);
  const transactions = useMemo(
    () => buildTransactionHistory(drops, userProfile).slice(0, 10),
    [drops, userProfile]
  );

  const monthNet = earningsDoc?.monthToDate ?? monthStats.net;
  const totalNet = earningsDoc?.totalEarned ?? totalStats.net;

  if (loading) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading earnings…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout>
      <div className="cs-page-eyebrow">Creator Studio</div>
      <h1 className="cs-page-title" style={{ marginBottom: 32 }}>
        Your <em>Earnings</em>
        {founding && <span className="cs-founding-badge">✦ Founding Rate · 10%</span>}
      </h1>

      <div className="cs-earnings-hero">
        <div className="cs-earnings-hero-dots" />
        <div className="cs-earn-block">
          <div className="cs-earn-block-label">Total earned</div>
          <div className="cs-earn-block-num">{formatMoneyWhole(totalNet)}</div>
          <div className="cs-earn-block-sub">All time · CAD</div>
        </div>
        <div className="cs-earn-block-divider" />
        <div className="cs-earn-block">
          <div className="cs-earn-block-label">This month</div>
          <div className="cs-earn-block-num">{formatMoneyWhole(monthNet)}</div>
          <div className="cs-earn-block-sub">{new Date().toLocaleDateString("en-CA", { month: "long", year: "numeric" })}</div>
        </div>
        <div className="cs-earn-block">
          <div className="cs-payout-card-inner">
            <div className="cs-earn-block-label">Next payout</div>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 500, color: "#fff", marginBottom: 4 }}>
              {nextPayoutDate()}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "var(--gold)", marginBottom: 8 }}>{formatMoney(monthNet)}</div>
            <button type="button" className="cs-btn cs-btn-primary" style={{ fontSize: 12 }}>
              Payout details
            </button>
          </div>
        </div>
      </div>

      <div className="cs-breakdown-grid">
        <div className="cs-card">
          <div className="cs-card-title" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
            This month breakdown
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Gross revenue</span>
            <span className="cs-breakdown-value">{formatMoney(monthStats.gross)}</span>
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Platform fee ({Math.round(monthStats.feeRate * 100)}% — founding rate)</span>
            <span className="cs-breakdown-value red">−{formatMoney(monthStats.platformFee)}</span>
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Stripe processing fee</span>
            <span className="cs-breakdown-value muted">−{formatMoney(monthStats.stripeFee)}</span>
          </div>
          <div className="cs-breakdown-row" style={{ paddingTop: 14 }}>
            <span className="cs-breakdown-label" style={{ fontWeight: 700 }}>You receive</span>
            <span className="cs-breakdown-value gold" style={{ fontSize: 18 }}>{formatMoney(monthStats.net)}</span>
          </div>
        </div>

        <div className="cs-card">
          <div className="cs-card-title" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Your founding rate
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Your fee rate</span>
            <span className="cs-breakdown-value gold">10% — locked forever</span>
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Standard rate</span>
            <span className="cs-breakdown-value muted">Will increase for new creators</span>
          </div>
          <div className="cs-breakdown-row">
            <span className="cs-breakdown-label">Your saving vs standard</span>
            <span className="cs-breakdown-value" style={{ color: "var(--green)" }}>Guaranteed for life</span>
          </div>
          {founding && (
            <div className="cs-founding-note">
              As a founding creator your 10% rate is <strong>permanently locked</strong> in your Terms of Service. It will never increase regardless of future pricing changes.
            </div>
          )}
        </div>
      </div>

      <div className="cs-card-title" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
        Transaction history
      </div>

      <div className="cs-drops-table">
        <div className="cs-drops-header" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr" }}>
          <span>Drop</span>
          <span>Gross</span>
          <span>Your cut</span>
          <span>Date</span>
        </div>
        {transactions.length === 0 ? (
          <div className="cs-empty">No paid transactions yet.</div>
        ) : (
          transactions.map((tx) => {
            const net = tx.isFree ? 0 : calculateCreatorReceive(tx.gross, userProfile).net;
            return (
              <div key={tx.id} className="cs-drop-row" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr" }}>
                <div>
                  <div className="cs-drop-title">{tx.title}</div>
                  <div className="cs-drop-meta">
                    {tx.isFree ? "Free drop" : `${tx.unlocks} unlocks · $${tx.price.toFixed(2)} each`}
                  </div>
                </div>
                <div className="cs-earn-amount">{formatMoney(tx.gross)}</div>
                <div className="cs-earn-amount" style={{ color: "var(--green)" }}>{formatMoney(net)}</div>
                <div className="cs-earn-sub">{timeAgo(tx.date)}</div>
              </div>
            );
          })
        )}
      </div>
    </CreatorStudioLayout>
  );
}
